import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { useFormState } from 'react-final-form';

import { Button } from 'UIPackage/MainModule/components/Button';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';

import { getPublishRulesFormRef } from '../selectors/publishRulesSelectors';
import { PublishRulesFormRef } from '../reducers/publishRulesReducer';
import { PUBLISH_RULES_ADDITIONAL_SETTINGS_BLOCK_ID } from '../constants/publishRulesFieldsElementsIds';
import { IPublishRulesFormAdditionalSetting } from '../interfaces/IPublishRulesFormAdditionalSetting';
import styles from './PublishRulesFormAdditionalSettings.module.scss';
import { PublishRulesKnmUnscheduledFormBlockNamesEnum } from '../enums/publishRulesKnmUnscheduledFormBlockNamesEnum';
import { getPublishDeadlineOptions } from '../common/getPublishDeadlineOptions';

interface IPublishRulesFormAdditionalSettingsProps {
  formRef?: PublishRulesFormRef;
  blockName?: PublishRulesKnmUnscheduledFormBlockNamesEnum;
  showDeadline?: boolean;
  showStatus?: boolean;
  showDate?: boolean;
  statusesSelectOptions?: ISelectOption[];
  reasonTypesSelectOptions?: ISelectOption[];
}

const PublishRulesFormAdditionalSettingsWithoutStore = ({
  formRef,
  blockName,
  showDate = true,
  showDeadline = false,
  showStatus = true,
  statusesSelectOptions,
  reasonTypesSelectOptions,
}: IPublishRulesFormAdditionalSettingsProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const name = blockName ? `${blockName}.additionalSettings` : 'additionalSettings';

  const publishDeadlineSelectOptions = useMemo(() => getPublishDeadlineOptions(), []);

  const { values } = useFormState();

  const addField = useCallback(() => {
    if (push) {
      const addingField: IPublishRulesFormAdditionalSetting = {
        ...(showDate ? { publicationDate: undefined } : {}),
        ...(showDeadline ? { publishDeadline: undefined } : {}),
        typeOfBasis: undefined,
        ...(showStatus ? { uponAttainingStatus: undefined } : {}),
      };

      push(name, addingField);
    }
  }, [push]);

  const makeRemoveField = (index: number) => () => remove && remove(name, index);

  const currentSettings: IPublishRulesFormAdditionalSetting[] = values?.[name] || [];

  // Не даем добавлять строк больше чем типов оснований
  const isAddDisabled =
    reasonTypesSelectOptions !== undefined ? currentSettings?.length >= reasonTypesSelectOptions?.length : true;

  return (
    <FieldBlock
      label="Дополнительные правила с учетом типа основания проведения мероприятия"
      id={PUBLISH_RULES_ADDITIONAL_SETTINGS_BLOCK_ID}
      button={
        <Button
          className="shared-btn-add"
          disabled={isAddDisabled}
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          onClick={addField}
        />
      }
    >
      <FieldArray name={name}>
        {({ fields }: FieldArrayRenderProps<IPublishRulesFormAdditionalSetting, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <table className={styles.Table}>
              <thead className={styles.TableHeader}>
                <tr>
                  <th>ТИП ОСНОВАНИЯ</th>
                  {showStatus && <th>ПО ДОСТИЖЕНИИ СТАТУСА</th>}
                  {showDate && <th>ДАТА ПУБЛИКАЦИИ</th>}
                  {showDeadline && <th>СРОК ПУБЛИКАЦИИ</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {fields.map((fieldName, index) => {
                  // Фильтруем уже использованные типы оснований
                  const usedValues = currentSettings
                    .map((item, i) => i !== index && item?.typeOfBasis)
                    .filter((v): v is ISelectOption => Boolean(v));

                  const availableReasonTypes = reasonTypesSelectOptions?.filter(
                    opt => !usedValues.find(itm => itm.value === opt.value),
                  );

                  return (
                    <tr key={`publish-rules-form-additional-settings-row-${index}`} className={styles.TableRow}>
                      <td>
                        <SelectInputField
                          name={`${fieldName}.typeOfBasis`}
                          options={availableReasonTypes}
                          placeholder="Выберите тип основания"
                          validate={makeRequired(REQUIRED_ERROR)}
                          isClearable={false}
                        />
                      </td>
                      {showStatus && (
                        <td>
                          <SelectInputField
                            name={`${fieldName}.uponAttainingStatus`}
                            isMulti
                            options={statusesSelectOptions}
                            placeholder="Выберите статус"
                            validate={makeRequired(REQUIRED_ERROR)}
                          />
                        </td>
                      )}
                      {showDate && (
                        <td>
                          <DatePickerField
                            name={`${fieldName}.publicationDate`}
                            placeholder="Выберите дату"
                            validate={makeRequired(REQUIRED_ERROR)}
                            dateFormat="dd.MM"
                            showCalendarYear={false}
                            placeholderText="Выберите день и месяц"
                          />
                        </td>
                      )}
                      {showDeadline && (
                        <td>
                          <SelectInputField
                            name={`${fieldName}.publishDeadline`}
                            placeholder="Выберите срок"
                            options={publishDeadlineSelectOptions}
                            validate={makeRequired(REQUIRED_ERROR)}
                          />
                        </td>
                      )}
                      <td>
                        <CloseButton onClick={makeRemoveField(index)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState) => ({
  formRef: getPublishRulesFormRef(state),
});

export const PublishRulesFormAdditionalSettings = connect(mapStateToProps)(
  PublishRulesFormAdditionalSettingsWithoutStore,
);
